/**
 * Stock BIN label derivation (bin-labels plan v1 PR-2) — pure. One label per stocked shelf identity
 * (per size), rendered by `bin-label-sheet.tsx` at the same two printer geometries as the hem tags.
 *
 * Deliberate content decisions:
 *  - The QR carries `s1|<binId>` (the contracts codec), NEVER the stockKey — a raw key is QR v11–13,
 *    physically unscannable at 17–20 mm (plan § 3.1). `binId` comes off the server view verbatim; a
 *    row without one (an old cached response mid-rollout) yields no label rather than a wrong one.
 *  - NO on-hand count on paper. A printed count is stale the moment a pick lands; the label is pure
 *    identity, and the QR answers "how many" live at the scan station.
 *  - SIZE is the headline (the most-grabbed-wrong attribute — the divider's lesson), then the blank,
 *    then one row per placement: style code where the design has one, else the design name, else a
 *    designId stub (a label must always print — see the browse page's same fallback chain).
 *  - Placements sort by location, matching the stockKey's own canonical order, and cap at three rows
 *    with an explicit `+N MORE` (an overflowing row list would be clipped SILENTLY by the card's
 *    `overflow: hidden`, which is exactly the failure mode the house rules exist to avoid).
 */
import { encodeStockBinCode } from '@fsp/contracts';
import { stripStyleCode } from './design-lib';
import type { GarmentStockPlacement, GarmentStockRow } from './garment-stock-lib';

/** One placement's printable text: the design NAME (owner ask 2026-09-07: staff know designs by
 *  name; the code already rides under the QR), with the code stripped off a name that leads with it
 *  ("LT-045 Ford Nation" prints as "Ford Nation" — the BackRow rule). A nameless design falls back
 *  to its style code, then an id stub — a label always prints. `isCode` marks that fallback
 *  (rendered mono, so it reads as a code at a glance). */
export type BinLabelText = {
  text: string;
  isCode: boolean;
};

export type BinLabel = {
  binId: string;
  /** The QR payload — `encodeStockBinCode(binId)`. */
  code: string;
  /**
   * The text printed under the QR — the dominant STYLE CODE (owner ask, 2026-09-07: "a hex blob is
   * the least useful thing to make big"): the back placement's code first (the back is the custom
   * print; the front is usually the brand mark — the browse page's same face heuristic), else the
   * first placement with a code, else the binId (still unique, and it matches the pick dialog's
   * `Bin <id>` copy). The hex always rides IN the QR either way.
   */
  qrCaption: string;
  /** The DOMINANT design's name, bold on the label (owner pick: layout option 1, 2026-09-07) —
   *  the back placement first (the custom print), else the first placement by location order. */
  primary: BinLabelText;
  /** Every other placement's name on one small line, "+ "-prefixed and " + "-joined; absent for a
   *  single-placement shirt. The line ellipsizes rather than wraps — visible truncation over a
   *  silent clip. No BACK/FRONT words anywhere (owner ask). */
  othersLine?: string;
  sizeName?: string;
  /** Chip text — 'TEE' | 'HOODIE'; absent when the catalog never said. */
  kind?: string;
  /** Brand + style, ` · ` colour — same composition as the server's `garmentStockBlankLabel`
   *  (scan resolve + the match identity), minus the size, which is the label's own headline. */
  blankLabel: string;
};

const KIND_CHIP: Record<string, string> = { tshirt: 'TEE', hoodie: 'HOODIE' };

function toText(p: GarmentStockPlacement, styleCodes: ReadonlyMap<string, string>): BinLabelText {
  const styleCode = styleCodes.get(p.designId);
  const name = stripStyleCode(p.designName ?? '', styleCode);
  const fallback = styleCode ?? p.designId.slice(0, 8);
  return { text: name || fallback, isCode: !name };
}

/** The label for one shelf row, or null when the row carries no `binId` (a pre-rollout response). */
export function binLabelForRow(
  row: GarmentStockRow,
  styleCodes: ReadonlyMap<string, string>,
): BinLabel | null {
  if (!row.binId) return null;
  const name = [row.brandName, row.styleName].filter(Boolean).join(' ');
  const blankLabel = [name, row.colorName].filter(Boolean).join(' · ') || row.ssSku;
  const sorted = [...row.placements].sort((a, b) => a.location.localeCompare(b.location));
  const backCode = styleCodes.get(sorted.find((p) => p.location === 'back')?.designId ?? '');
  const anyCode = sorted.map((p) => styleCodes.get(p.designId)).find(Boolean);
  // The dominant placement carries the bold name: back first (the custom print — the browse page's
  // same face heuristic), else the first by canonical location order.
  const dominant = sorted.find((p) => p.location === 'back') ?? sorted[0];
  const others = sorted.filter((p) => p !== dominant).map((p) => toText(p, styleCodes).text);
  return {
    binId: row.binId,
    code: encodeStockBinCode(row.binId),
    qrCaption: backCode ?? anyCode ?? row.binId,
    primary: dominant ? toText(dominant, styleCodes) : { text: row.binId, isCode: true }, // schema guarantees ≥1 placement; belt-and-braces
    othersLine: others.length > 0 ? `+ ${others.join(' + ')}` : undefined,
    sizeName: row.sizeName,
    kind: row.apparelKind ? KIND_CHIP[row.apparelKind] : undefined,
    blankLabel,
  };
}

/** Labels for a print run — rows without a binId are skipped (reported by the sheet's toolbar). */
export function binLabelsForRows(
  rows: readonly GarmentStockRow[],
  styleCodes: ReadonlyMap<string, string>,
): { labels: BinLabel[]; skipped: number } {
  const labels: BinLabel[] = [];
  let skipped = 0;
  for (const row of rows) {
    const label = binLabelForRow(row, styleCodes);
    if (label) labels.push(label);
    else skipped += 1;
  }
  return { labels, skipped };
}
